import { Button } from "@/components/chadcn/Button"
import { Checkbox } from "@/components/chadcn/Checkbox"
import { Label } from "@/components/chadcn/Label"
import { TextInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSelfQuery } from "@/data/queries"
import {
  useObservationByIdQuery,
  useUpdateBloodGlucoseFormMutation,
} from "@/data/queries/measurementQueries"
import type { BloodGlucoseForm } from "@/model/BloodGlucoseForm"
import type { BloodGlucoseObservation } from "@/model/BloodGlucoseObservation"
import type { QuickCheckReason } from "@/model/QuickCheckReason"
import { quickCheckReasons } from "@/model/QuickCheckReason"
import { ROUTES } from "@/routes"
import { quickCheckSchema, type QuickCheckFormData } from "@/schemas/measurements"
import { zodResolver } from "@hookform/resolvers/zod"
import { Controller, useForm } from "react-hook-form"
import { useLocation, useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import DateTimePicker from "./components/DateTimePicker"
import NotesField from "./components/NotesField"

const reasonLabels: Record<QuickCheckReason, string> = {
  curious: "Curious",
  "medication-change": "Medication Change",
  tired: "Tired",
  dizzy: "Dizzy",
  "light-headed": "Light Headed",
  nausea: "Nausea",
  "decreased-fetal-movement": "Decreased fetal movement",
  other: "Other",
}

// Rendered only once observation and form are loaded
const QuickCheckEditForm = ({
  observation,
  form,
  patientId,
  returnUrl,
}: {
  observation: BloodGlucoseObservation
  form: BloodGlucoseForm
  patientId: string
  returnUrl: string
}) => {
  const navigate = useNavigate()
  const { mutateAsync: updateForm, isPending } = useUpdateBloodGlucoseFormMutation({
    patientId,
    formId: form.id,
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<QuickCheckFormData>({
    mode: "onSubmit",
    resolver: zodResolver(quickCheckSchema),
    defaultValues: {
      bloodGlucoseInMilligramsPerDeciliter:
        observation.reading?.bloodGlucoseInMilligramsPerDeciliter,
      dateTime: form.dateTime.toString(),
      reasons: form.reasons ?? [],
      notes: form.notes ?? undefined,
    },
  })

  const onSubmit = async (formData: QuickCheckFormData) => {
    try {
      await updateForm({
        formId: form.id,
        update: {
          notes: formData.notes ?? null,
          reasons: formData.reasons,
        },
      })
      navigate(returnUrl)
    } catch {
      toast.error("Failed to save measurement. Please try again.")
    }
  }

  return (
    <div className="flex flex-col gap-6 px-4 py-8">
      <h1 className="text-2xl font-bold text-primary-contrast">Edit quick check</h1>

      {!form.editable && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <p className="font-semibold">Unable to edit form</p>
          <p className="mt-1">
            Your doctor has already reviewed your data. It is no longer possible to make changes.
          </p>
        </div>
      )}

      <form
        id="edit-quick-check-form"
        className="flex flex-col gap-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="bloodGlucoseInMilligramsPerDeciliter">Blood glucose (mg/dL)</Label>
          <TextInput
            id="bloodGlucoseInMilligramsPerDeciliter"
            type="number"
            min={20}
            max={800}
            step={1}
            disabled={isPending || !form.editable}
            isError={!!errors.bloodGlucoseInMilligramsPerDeciliter}
            placeholder="e.g. 95"
            {...register("bloodGlucoseInMilligramsPerDeciliter", { valueAsNumber: true })}
          />
          {errors.bloodGlucoseInMilligramsPerDeciliter && (
            <p className="text-sm text-error">
              {errors.bloodGlucoseInMilligramsPerDeciliter.message}
            </p>
          )}
        </div>

        <Controller
          name="dateTime"
          control={control}
          render={({ field }) => (
            <DateTimePicker
              value={field.value}
              onChange={field.onChange}
              disabled={isPending || !form.editable}
            />
          )}
        />

        <div className="flex flex-col gap-2">
          <Label>Why are you checking?</Label>
          <Controller
            name="reasons"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                {quickCheckReasons.map((reason) => {
                  const isChecked = field.value.includes(reason)
                  return (
                    <label
                      key={reason}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-secondary px-4 py-3"
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            field.onChange([...field.value, reason])
                          } else {
                            field.onChange(field.value.filter((r) => r !== reason))
                          }
                        }}
                        disabled={isPending || !form.editable}
                      />
                      <span className="text-h4 font-medium text-primary-contrast">
                        {reasonLabels[reason]}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
          />
          {errors.reasons && <p className="text-sm text-error">{errors.reasons.message}</p>}
        </div>

        <NotesField
          disabled={isPending || !form.editable}
          isError={!!errors.notes}
          {...register("notes")}
        />
        {errors.notes && <p className="-mt-4 text-sm text-error">{errors.notes.message}</p>}
      </form>

      <Button
        type="submit"
        form="edit-quick-check-form"
        disabled={isPending || !form.editable}
        className="flex w-full gap-3 py-2 text-base"
      >
        {isPending && <Spinner />}
        Save reading
      </Button>
    </div>
  )
}

const EditQuickCheckPage = () => {
  const { measurementId } = useParams<{ measurementId: string }>()
  const location = useLocation()
  const state = location.state as { initialTab?: string } | null
  const returnUrl = `${ROUTES.DASHBOARD}?tab=${state?.initialTab ?? "measurement"}`
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""

  const { data: observation, isLoading } = useObservationByIdQuery({
    readingId: measurementId ?? null,
  })

  if (isLoading) {
    return (
      <Page showAppbar={false}>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Page>
    )
  }

  if (!observation) {
    return (
      <Page showAppbar={false}>
        <div className="px-4 py-8">
          <p className="text-primary-contrast/70">Measurement not found.</p>
        </div>
      </Page>
    )
  }

  if (!observation.form) {
    return (
      <Page showAppbar={false}>
        <div className="px-4 py-8">
          <h1 className="text-2xl font-bold text-primary-contrast">Edit quick check</h1>
          <p className="mt-4 text-primary-contrast/70">
            The form for this reading has been deleted and cannot be edited.
          </p>
        </div>
      </Page>
    )
  }

  return (
    <Page showAppbar={false}>
      <QuickCheckEditForm
        observation={observation}
        form={observation.form}
        patientId={patientId}
        returnUrl={returnUrl}
      />
    </Page>
  )
}

export default EditQuickCheckPage
